import { SurveyResponse, ScoreBreakdown, Question } from '@/types/survey';

interface SentimentResult {
  score: number;
  emotions: string[];
  keywords: string[];
  confidence: number;
  topicDistribution: Record<string, number>; // New field for topic analysis
  emotionIntensity: Record<string, number>; // New field for emotion intensity
}

interface PredictionResult {
  futureScore: number;
  timeline: string;
  confidenceLevel: number;
  improvementAreas: string[];
  projectedScores: {
    baseline: number[];
    withImprovements: number[];
    timeline: string[];
  }; // New field for trend visualization
}

interface CompetitiveAnalysis {
  position: string;
  strengths: string[];
  weaknesses: string[];
  opportunities: string[];
  industryBenchmarks: {
    category: string;
    yourScore: number;
    industryAverage: number;
    topPerformerScore: number;
  }[];
}

export const analyzeSentimentDetailed = (
  responses: SurveyResponse,
  questions: Question[]
): SentimentResult => {
  // This is a more advanced version of sentiment analysis
  // In a real implementation, this would use a more sophisticated NLP model
  
  const textResponses = Object.entries(responses)
    .filter(([_, answer]) => typeof answer === 'string' && answer.length > 10)
    .map(([id, answer]) => {
      const questionId = parseInt(id);
      const question = questions.find(q => q.id === questionId);
      return {
        id: questionId,
        question: question?.text || '',
        answer: answer as string
      };
    });
  
  // Extract potential emotions from text with improved detection
  const emotions = detectEmotionsEnhanced(textResponses.map(r => r.answer).join(' '));
  
  // Extract keywords from responses with better algorithm
  const keywords = extractKeywordsEnhanced(textResponses.map(r => r.answer).join(' '));
  
  // Calculate overall sentiment score (1-5) with improved algorithm
  const sentimentScore = calculateDetailedSentimentScore(textResponses);
  
  // Calculate emotion intensity for each detected emotion
  const emotionIntensity = calculateEmotionIntensity(textResponses.map(r => r.answer).join(' '), emotions);
  
  // Perform topic modeling to identify key themes
  const topicDistribution = performTopicModeling(textResponses.map(r => r.answer).join(' '));
  
  return {
    score: sentimentScore,
    emotions,
    keywords,
    confidence: 0.7 + Math.min(0.2, textResponses.length * 0.02), // Higher confidence with more responses
    emotionIntensity,
    topicDistribution
  };
};

export const generatePersonalizedRecommendations = (
  scoreBreakdown: ScoreBreakdown,
  industry: string
): string[] => {
  const recommendations: string[] = [];
  const scores = {
    ea: scoreBreakdown.ea.percentage / 100 * 5,
    elv: scoreBreakdown.elv.percentage / 100 * 5,
    l: scoreBreakdown.l.percentage / 100 * 5,
    tb: scoreBreakdown.tb.percentage / 100 * 5,
    bc: scoreBreakdown.bc.percentage / 100 * 5 // Added business continuity
  };
  
  // Get the lowest scoring categories for targeted recommendations
  const sortedCategories = Object.entries(scores).sort(([, a], [, b]) => a - b);
  const lowestCategories = sortedCategories.slice(0, 2);
  
  // Industry-specific recommendations with more personalization
  if (industry === 'Technology') {
    if (lowestCategories.some(([cat]) => cat === 'ea')) {
      if (scores.ea < 2.5) {
        recommendations.push("CRITICAL: Implement user experience tracking to address severe emotional disconnection in your tech platform");
      } else if (scores.ea < 3.5) {
        recommendations.push("Implement advanced user emotion tracking to understand pain points in the tech journey");
        recommendations.push("Create a tech ambassadors program to build community around your platform");
      }
    }
    if (lowestCategories.some(([cat]) => cat === 'elv')) {
      if (scores.elv < 2.5) {
        recommendations.push("URGENT: Your innovation pipeline is at risk - develop an open innovation initiative to rapidly address market relevance");
      } else if (scores.elv < 3.5) {
        recommendations.push("Develop an open innovation initiative to stay at the cutting edge of technology");
        recommendations.push("Create a technology roadmap that aligns with industry future trends");
      }
    }
    if (lowestCategories.some(([cat]) => cat === 'tb')) {
      recommendations.push("Implement transparent development practices with regular community updates");
    }
  } else if (industry === 'Healthcare') {
    if (lowestCategories.some(([cat]) => cat === 'tb')) {
      if (scores.tb < 3.0) { 
        recommendations.push("PRIORITY: Patient trust is critically low - implement immediate transparency measures while maintaining privacy compliance");
      } else if (scores.tb < 4.0) { // Higher standard for trust in healthcare
        recommendations.push("Improve patient data transparency while maintaining strict privacy compliance");
        recommendations.push("Publish regular trust reports showing how patient outcomes are prioritized");
      }
    }
    if (lowestCategories.some(([cat]) => cat === 'l')) {
      if (scores.l < 2.5) {
        recommendations.push("URGENT: Develop leadership continuity plans to address critical commitment issues");
      } else if (scores.l < 3.5) {
        recommendations.push("Create care continuity programs that maintain patient relationships long-term");
      }
    }
    if (lowestCategories.some(([cat]) => cat === 'ea')) {
      recommendations.push("Implement patient experience mapping to identify emotional touchpoints");
    }
  } 
  // ... keep existing code (other industry conditions)
  
  // Add more personalized recommendations based on combinations of low scores
  if (scores.ea < 3.5 && scores.tb < 3.5) {
    recommendations.push("Your emotional connection and trust issues are related - develop a transparent community engagement strategy that addresses both concerns simultaneously");
  }
  
  if (scores.elv < 3.5 && scores.l < 3.5) {
    recommendations.push("The connection between leadership vision and future relevance needs attention - align leadership messaging with innovation roadmap");
  }
  
  if (scores.ea < 3.0 && scores.bc < 3.0) {
    recommendations.push("CRITICAL: Low emotional connection is threatening business continuity - prioritize customer retention initiatives immediately");
  }
  
  // Only return up to 5 recommendations, prioritizing most impactful
  return recommendations.slice(0, 5);
};

export const predictFuturePerformance = (
  scoreBreakdown: ScoreBreakdown
): PredictionResult => {
  // Enhanced prediction algorithm with time-series projection
  const currentScore = scoreBreakdown.totalNEV;
  const potentialImprovement = Math.max(0, 50 - currentScore);
  
  // Predict future score with assumed improvements
  const improvementFactor = 0.2; // Assume 20% of the gap can be closed
  const futureScore = currentScore + (potentialImprovement * improvementFactor);
  
  // Determine timeline based on current score and improvement difficulty
  let timeline = '';
  if (currentScore < 25) {
    timeline = "8-12 months with significant resources";
  } else if (currentScore < 35) {
    timeline = "6-9 months with dedicated resources";
  } else if (currentScore < 42) {
    timeline = "3-6 months with focused improvements";
  } else {
    timeline = "1-3 months with targeted adjustments";
  }
  
  // Determine areas with highest improvement potential
  const scores = [
    { category: "Emotional Attachment", score: scoreBreakdown.ea.percentage },
    { category: "Ethical and Legacy Value", score: scoreBreakdown.elv.percentage },
    { category: "Longevity", score: scoreBreakdown.l.percentage },
    { category: "Trust Building", score: scoreBreakdown.tb.percentage },
    { category: "Business Continuity", score: scoreBreakdown.bc.percentage }
  ];
  
  // Sort by lowest score (highest improvement potential)
  const improvementAreas = scores
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
    .map(area => area.category);
  
  // Generate projected scores for trend visualization
  const timelinePoints = ["1 month", "2 months", "3 months", "6 months", "1 year"];
  
  // Baseline projection (no interventions)
  const baselineProjection = [
    currentScore,
    currentScore * 1.02,
    currentScore * 1.03,
    currentScore * 1.04,
    currentScore * 1.05
  ].map(score => Math.min(50, score));
  
  // Projection with improvements
  const withImprovementsProjection = [
    currentScore,
    currentScore + (potentialImprovement * 0.1),
    currentScore + (potentialImprovement * 0.15),
    currentScore + (potentialImprovement * 0.25),
    currentScore + (potentialImprovement * 0.4)
  ].map(score => Math.min(50, score));
  
  return {
    futureScore: parseFloat(futureScore.toFixed(1)),
    timeline,
    confidenceLevel: 0.7, // Fixed confidence for now
    improvementAreas,
    projectedScores: {
      baseline: baselineProjection.map(score => parseFloat(score.toFixed(1))),
      withImprovements: withImprovementsProjection.map(score => parseFloat(score.toFixed(1))),
      timeline: timelinePoints
    }
  };
};

export const analyzeCompetitivePosition = (
  scoreBreakdown: ScoreBreakdown,
  industry: string
): CompetitiveAnalysis => {
  // Enhanced competitive analysis with industry benchmarks
  const totalScore = scoreBreakdown.totalNEV;
  
  // Determine market position based on score ranges
  let position;
  if (totalScore < 30) {
    position = "Below industry average";
  } else if (totalScore < 40) {
    position = "At industry average";
  } else if (totalScore < 45) {
    position = "Above industry average";
  } else {
    position = "Industry leader";
  }
  
  // Determine strengths (highest scoring categories)
  const categories = [
    { name: "Emotional Connection", score: scoreBreakdown.ea.score / scoreBreakdown.ea.total * 100 },
    { name: "Ethical Foundations", score: scoreBreakdown.elv.score / scoreBreakdown.elv.total * 100 },
    { name: "Leadership Commitment", score: scoreBreakdown.l.score / scoreBreakdown.l.total * 100 },
    { name: "Trustworthiness", score: scoreBreakdown.tb.score / scoreBreakdown.tb.total * 100 },
    { name: "Business Continuity", score: scoreBreakdown.bc.score / scoreBreakdown.bc.total * 100 }
  ];
  
  const strengths = categories
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map(cat => cat.name);
  
  const weaknesses = categories
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
    .map(cat => cat.name);
  
  // Industry-specific opportunities
  const opportunities = getIndustryOpportunities(industry);
  
  // Generate industry benchmarks
  const industryBenchmarks = generateIndustryBenchmarks(scoreBreakdown, industry);
  
  return {
    position,
    strengths,
    weaknesses,
    opportunities: opportunities.slice(0, 3),
    industryBenchmarks
  };
};

// New helper functions for enhanced sentiment analysis
function detectEmotionsEnhanced(text: string): string[] {
  const emotions = [];
  const emotionKeywords = {
    'joy': ['love', 'happy', 'great', 'excellent', 'amazing', 'delighted', 'pleased', 'wonderful', 'fantastic', 'awesome', 'thrilled'],
    'trust': ['trust', 'reliable', 'confident', 'believe', 'consistent', 'dependable', 'faith', 'secure', 'honest', 'loyal'],
    'anticipation': ['expect', 'look forward', 'anticipate', 'future', 'potential', 'promising', 'upcoming', 'hope', 'eager', 'awaiting'],
    'interest': ['interested', 'curious', 'engaged', 'fascinating', 'intriguing', 'captivating', 'appealing', 'attention', 'drawn to'],
    'satisfaction': ['satisfied', 'content', 'fulfilled', 'gratified', 'pleased', 'meets expectations', 'quality', 'value'],
    'enthusiasm': ['enthusiastic', 'excited', 'passionate', 'energetic', 'motivated', 'inspired', 'eager', 'keen'],
    'anger': ['angry', 'frustrated', 'annoyed', 'irritated', 'upset', 'terrible', 'furious', 'outraged', 'mad', 'hostile'],
    'fear': ['afraid', 'worried', 'concerned', 'anxious', 'risky', 'dangerous', 'scared', 'frightened', 'nervous', 'uneasy'],
    'surprise': ['surprised', 'unexpected', 'amazed', 'astonished', 'shocked', 'startled', 'stunned', 'unpredicted', 'remarkable'],
    'sadness': ['sad', 'disappointed', 'unhappy', 'regret', 'miss', 'unfortunate', 'depressed', 'gloomy', 'heartbroken'],
    'disgust': ['disgusting', 'awful', 'horrible', 'unpleasant', 'offensive', 'repulsive', 'distasteful', 'revolting'],
    'confusion': ['confused', 'uncertain', 'unclear', 'puzzled', 'perplexed', 'ambiguous', 'complicated', 'complex', 'difficult']
  };
  
  const textLower = text.toLowerCase();
  const words = textLower.split(/\W+/);
  const emotionCounts: Record<string, number> = {};
  
  // Check for each emotion with more sophisticated detection
  Object.entries(emotionKeywords).forEach(([emotion, keywords]) => {
    let count = 0;
    keywords.forEach(keyword => {
      // Check for exact word match or phrases
      if (keyword.includes(' ')) {
        if (textLower.includes(keyword)) count++;
      } else {
        // For single words, check for whole word matches
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (words.some(word => regex.test(word))) count++;
      }
    });
    
    if (count > 0) {
      emotionCounts[emotion] = count;
    }
  });
  
  // Sort emotions by frequency and take the top 3
  const sortedEmotions = Object.entries(emotionCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .map(([emotion]) => emotion);
  
  emotions.push(...sortedEmotions.slice(0, 3));
  
  // If no emotions detected, add 'neutral'
  if (emotions.length === 0) {
    emotions.push('neutral');
  }
  
  return emotions;
}

function extractKeywordsEnhanced(text: string): string[] {
  // Enhanced keyword extraction with TF-IDF like approach
  const words = text.toLowerCase().split(/\W+/);
  
  // Filter out common words and short words
  const stopWords = ['the', 'a', 'an', 'and', 'or', 'but', 'is', 'are', 'was', 'were', 
                  'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did',
                  'to', 'from', 'in', 'out', 'on', 'off', 'over', 'under', 'again',
                  'further', 'then', 'once', 'here', 'there', 'when', 'where', 'why',
                  'how', 'all', 'any', 'both', 'each', 'few', 'more', 'most', 'other',
                  'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so',
                  'than', 'too', 'very', 's', 't', 'can', 'will', 'just', 'don',
                  'should', 'now', 'i', 'me', 'my', 'myself', 'we', 'our', 'ours',
                  'ourselves', 'you', 'your', 'yours', 'yourself', 'yourselves',
                  'he', 'him', 'his', 'himself', 'she', 'her', 'hers', 'herself',
                  'it', 'its', 'itself', 'they', 'them', 'their', 'theirs', 'themselves',
                  'what', 'which', 'who', 'whom', 'this', 'that', 'these', 'those',
                  'am', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have',
                  'has', 'had', 'having', 'do', 'does', 'did', 'doing', 'would',
                  'should', 'could', 'ought', 'i\'m', 'you\'re', 'he\'s', 'she\'s',
                  'it\'s', 'we\'re', 'they\'re', 'i\'ve', 'you\'ve', 'we\'ve',
                  'they\'ve', 'i\'d', 'you\'d', 'he\'d', 'she\'d', 'we\'d',
                  'they\'d', 'i\'ll', 'you\'ll', 'he\'ll', 'she\'ll', 'we\'ll',
                  'they\'ll', 'isn\'t', 'aren\'t', 'wasn\'t', 'weren\'t', 'hasn\'t',
                  'haven\'t', 'hadn\'t', 'doesn\'t', 'don\'t', 'didn\'t', 'won\'t',
                  'wouldn\'t', 'shan\'t', 'shouldn\'t', 'can\'t', 'cannot', 'couldn\'t',
                  'mustn\'t', 'let\'s', 'that\'s', 'who\'s', 'what\'s', 'here\'s',
                  'there\'s', 'when\'s', 'where\'s', 'why\'s', 'how\'s', 'use', 'used', 'using'];
  
  const filteredWords = words
    .filter(word => word.length > 3 && !stopWords.includes(word));
  
  // Calculate word frequencies  
  const wordFrequencies: {[key: string]: number} = {};
  filteredWords.forEach(word => {
    wordFrequencies[word] = (wordFrequencies[word] || 0) + 1;
  });
  
  // Extract bigrams (two-word phrases)
  const bigrams: {[key: string]: number} = {};
  for (let i = 0; i < filteredWords.length - 1; i++) {
    const bigram = `${filteredWords[i]} ${filteredWords[i+1]}`;
    bigrams[bigram] = (bigrams[bigram] || 0) + 1;
  }
  
  // Combine unigrams and bigrams, give priority to meaningful bigrams
  const allKeywords = [
    ...Object.entries(wordFrequencies)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15)
      .map(entry => entry[0]),
    ...Object.entries(bigrams)
      .filter(([, count]) => count > 1) // Only include bigrams that appear more than once
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(entry => entry[0])
  ];
  
  // Remove duplicates and take top keywords
  const uniqueKeywords = [...new Set(allKeywords)];
  return uniqueKeywords.slice(0, 12);
}

function calculateDetailedSentimentScore(textResponses: {id: number, question: string, answer: string}[]): number {
  // Enhanced sentiment analysis with context awareness
  
  const positiveWords = ['good', 'great', 'excellent', 'love', 'enjoy', 'easy', 'helpful',
                         'best', 'amazing', 'outstanding', 'fantastic', 'awesome',
                         'wonderful', 'perfect', 'brilliant', 'superb', 'delighted',
                         'pleased', 'satisfied', 'impressive', 'innovative', 'efficient',
                         'effective', 'valuable', 'useful', 'quality', 'exceptional'];
                         
  const negativeWords = ['bad', 'poor', 'terrible', 'hate', 'difficult', 'confusing', 
                         'frustrating', 'worst', 'awful', 'horrible', 'disappointing',
                         'useless', 'annoying', 'complicated', 'unreliable', 'failure',
                         'mess', 'problem', 'issue', 'defective', 'broken', 'lacking',
                         'inadequate', 'inefficient', 'mediocre', 'subpar', 'fails'];
  
  const intensifiers = ['very', 'extremely', 'absolutely', 'completely', 'totally', 'highly', 
                        'incredibly', 'remarkably', 'exceptionally', 'exceedingly'];
  
  const negations = ['not', 'no', "don't", "doesn't", "didn't", "won't", "wouldn't", 
                     "can't", "cannot", "couldn't", 'never', 'neither', 'nor'];
  
  let totalWeight = 0;
  let weightedScore = 0;
  
  textResponses.forEach(response => {
    const text = response.answer.toLowerCase();
    const words = text.split(/\W+/);
    let localScore = 3; // Neutral starting point
    
    // Context-based analysis
    let posCount = 0;
    let negCount = 0;
    let intensifierPresent = false;
    let negationPresent = false;
    
    // Check for negations and intensifiers first
    negationPresent = negations.some(neg => words.includes(neg));
    intensifierPresent = intensifiers.some(int => words.includes(int));
    
    // Count positive and negative words
    posCount = positiveWords.filter(word => text.includes(word)).length;
    negCount = negativeWords.filter(word => text.includes(word)).length;
    
    // Apply negation effects (more sophisticated approach)
    if (negationPresent) {
      // If there are negations, we need to analyze more carefully
      const segments = text.split(/\b(not|no|don't|doesn't|didn't|won't|wouldn't|can't|cannot|couldn't|never)\b/i);
      
      // Reset counts for more accurate analysis
      posCount = 0;
      negCount = 0;
      
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        // Check if this segment is a negation
        const isNegation = negations.some(neg => segment.toLowerCase() === neg);
        
        // If this is a negation, the next segment should have its sentiment flipped
        if (isNegation && i + 1 < segments.length) {
          const nextSegment = segments[i + 1];
          // Count negated positive words as negative
          const negatedPosCount = positiveWords.filter(word => nextSegment.includes(word)).length;
          // Count negated negative words as positive
          const negatedNegCount = negativeWords.filter(word => nextSegment.includes(word)).length;
          
          negCount += negatedPosCount;
          posCount += negatedNegCount;
        } else if (!isNegation && i === 0 || (i > 0 && !negations.some(neg => segments[i-1].toLowerCase() === neg))) {
          // For non-negated segments, count normally
          posCount += positiveWords.filter(word => segment.includes(word)).length;
          negCount += negativeWords.filter(word => segment.includes(word)).length;
        }
      }
    }
    
    // Calculate base sentiment direction
    if (posCount > negCount) {
      localScore += Math.min(2, Math.log(posCount + 1) * 0.8);
    } else if (negCount > posCount) {
      localScore -= Math.min(2, Math.log(negCount + 1) * 0.8);
    }
    
    // Apply intensifier effect if present
    if (intensifierPresent) {
      if (localScore > 3) localScore = Math.min(5, localScore + 0.5);
      if (localScore < 3) localScore = Math.max(1, localScore - 0.5);
    }
    
    // Weight longer responses more heavily
    const weightFactor = Math.min(1.5, 0.5 + (response.answer.length / 500));
    weightedScore += localScore * weightFactor;
    totalWeight += weightFactor;
  });
  
  // Average the scores, with a minimum of 1 and maximum of 5
  return Math.max(1, Math.min(5, totalWeight > 0 ? weightedScore / totalWeight : 3));
}

// New helper function for emotion intensity analysis
function calculateEmotionIntensity(text: string, emotions: string[]): Record<string, number> {
  const result: Record<string, number> = {};
  
  // Skip calculation if text is too short
  if (text.length < 20) {
    emotions.forEach(emotion => {
      result[emotion] = 0.5; // Neutral intensity
    });
    return result;
  }
  
  // Define emotion-specific intensity indicators
  const emotionIntensifiers: Record<string, string[]> = {
    'joy': ['extremely', 'incredibly', 'absolutely', 'very', 'so', 'really'],
    'trust': ['complete', 'absolute', 'total', 'deeply', 'firmly'],
    'anticipation': ['eagerly', 'excitedly', 'impatiently', 'desperately'],
    'anger': ['furious', 'enraged', 'outraged', 'extremely', 'absolutely'],
    'fear': ['terrified', 'panicked', 'extremely', 'deeply', 'severely'],
    'surprise': ['completely', 'totally', 'absolutely', 'utterly', 'deeply'],
    'sadness': ['devastated', 'heartbroken', 'deeply', 'profoundly', 'extremely'],
    'disgust': ['utterly', 'extremely', 'totally', 'completely', 'thoroughly']
  };
  
  // Calculate intensity for each emotion
  emotions.forEach(emotion => {
    // Default medium intensity
    let intensity = 0.5;
    
    // Check for emotion-specific intensifiers
    if (emotionIntensifiers[emotion]) {
      const intensifiers = emotionIntensifiers[emotion];
      const intensifierCount = intensifiers.filter(word => text.toLowerCase().includes(word)).length;
      
      // Increase intensity based on intensifiers found
      intensity += Math.min(0.4, intensifierCount * 0.1);
    }
    
    // Check for emotional repetition (e.g., "very very happy")
    const repetitionPatterns = ['very very', 'really really', 'so so', 'extremely extremely'];
    if (repetitionPatterns.some(pattern => text.toLowerCase().includes(pattern))) {
      intensity += 0.1;
    }
    
    // Check for exclamation marks
    const exclamationCount = (text.match(/!/g) || []).length;
    intensity += Math.min(0.3, exclamationCount * 0.05);
    
    // Ensure intensity is between 0.1 and 1.0
    result[emotion] = Math.max(0.1, Math.min(1.0, intensity));
  });
  
  return result;
}

// Topic modeling function
function performTopicModeling(text: string): Record<string, number> {
  // This is a simplified topic modeling approach
  // In a real implementation, use LDA, NMF or transformer-based topic modeling
  
  const topics: Record<string, string[]> = {
    'Product Quality': ['quality', 'reliable', 'durability', 'robust', 'solid', 'dependable', 'construction'],
    'User Experience': ['experience', 'interface', 'usability', 'intuitive', 'easy', 'accessible', 'frustrating'],
    'Customer Service': ['service', 'support', 'responsive', 'helpful', 'assistance', 'representative', 'contact'],
    'Value Proposition': ['value', 'price', 'worth', 'cost', 'expensive', 'affordable', 'premium'],
    'Innovation': ['innovative', 'unique', 'creative', 'new', 'breakthrough', 'cutting-edge', 'novel'],
    'Reliability': ['reliable', 'consistent', 'dependable', 'stable', 'trustworthy', 'predictable'],
    'Performance': ['performance', 'speed', 'efficient', 'powerful', 'fast', 'responsive', 'slow'],
    'Features': ['features', 'functionality', 'options', 'capabilities', 'tools', 'functions'],
    'Design': ['design', 'look', 'appearance', 'aesthetic', 'beautiful', 'elegant', 'attractive'],
    'Community': ['community', 'network', 'users', 'audience', 'people', 'members', 'group']
  };
  
  const result: Record<string, number> = {};
  const textLower = text.toLowerCase();
  const totalWords = text.split(/\W+/).length;
  
  // Skip if text is too short
  if (totalWords < 10) {
    return { 'General': 1.0 };
  }
  
  // Calculate topic scores
  Object.entries(topics).forEach(([topic, keywords]) => {
    let score = 0;
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      const matches = textLower.match(regex) || [];
      score += matches.length;
    });
    
    // Normalize score based on text length
    const normalizedScore = score / Math.sqrt(totalWords);
    if (normalizedScore > 0.01) {
      result[topic] = parseFloat(normalizedScore.toFixed(2));
    }
  });
  
  // If no topics found, return General
  if (Object.keys(result).length === 0) {
    return { 'General': 1.0 };
  }
  
  // Normalize topics to sum to 1.0
  const totalScore = Object.values(result).reduce((acc, val) => acc + val, 0);
  Object.keys(result).forEach(topic => {
    result[topic] = parseFloat((result[topic] / totalScore).toFixed(2));
  });
  
  return result;
}

// Industry opportunities helper
function getIndustryOpportunities(industry: string): string[] {
  switch(industry) {
    case 'Technology':
      return [
        "AI integration for personalized user journeys",
        "Developer community engagement programs",
        "Open-source contribution strategies",
        "API ecosystem development"
      ];
    case 'Healthcare':
      return [
        "Patient-centered care initiatives",
        "Telehealth expansion opportunities",
        "Preventative health partnerships",
        "Health data analytics enhancement"
      ];
    case 'Education':
      return [
        "Hybrid learning model development",
        "Career pathway partnerships",
        "Personalized learning algorithms",
        "Educator empowerment programs"
      ];
    case 'Finance':
      return [
        "Financial wellness tools integration",
        "Sustainable investing initiatives",
        "Transparent fee structure implementation",
        "Digital banking experience enhancement"
      ];
    case 'Entertainment':
      return [
        "Interactive content experiences",
        "Creator economy integration",
        "Community-driven content development",
        "Cross-platform content strategies"
      ];
    case 'Retail':
      return [
        "Omnichannel experience integration",
        "Personalized shopping journeys",
        "Loyalty program enhancement",
        "Sustainable retail practices"
      ];
    case 'Manufacturing':
      return [
        "Supply chain transparency initiatives",
        "Sustainable production methods",
        "Smart factory implementation",
        "Worker safety enhancement programs"
      ];
    case 'Web 3.0':
      return [
        "Decentralized community building",
        "Token utility enhancement",
        "Cross-chain interoperability",
        "Real-world asset integration"
      ];
    default:
      return [
        "Customer experience enhancement programs",
        "Digital transformation initiatives",
        "Community building strategies",
        "Sustainability integration opportunities"
      ];
  }
}

// Industry benchmarks helper
function generateIndustryBenchmarks(scoreBreakdown: ScoreBreakdown, industry: string): { category: string; yourScore: number; industryAverage: number; topPerformerScore: number; }[] {
  // Define industry averages and top performer benchmarks
  const industryAverages: Record<string, Record<string, number>> = {
    'Technology': {
      'Emotional Connection': 62,
      'Ethical Foundations': 58,
      'Leadership Commitment': 65,
      'Trustworthiness': 60,
      'Business Continuity': 67
    },
    'Healthcare': {
      'Emotional Connection': 68,
      'Ethical Foundations': 72,
      'Leadership Commitment': 65,
      'Trustworthiness': 78,
      'Business Continuity': 70
    },
    'Finance': {
      'Emotional Connection': 55,
      'Ethical Foundations': 67,
      'Leadership Commitment': 62,
      'Trustworthiness': 65,
      'Business Continuity': 75
    },
    'Education': {
      'Emotional Connection': 72,
      'Ethical Foundations': 70,
      'Leadership Commitment': 64,
      'Trustworthiness': 72,
      'Business Continuity': 62
    },
    'Entertainment': {
      'Emotional Connection': 75,
      'Ethical Foundations': 58,
      'Leadership Commitment': 60,
      'Trustworthiness': 62,
      'Business Continuity': 64
    },
    'Retail': {
      'Emotional Connection': 65,
      'Ethical Foundations': 60,
      'Leadership Commitment': 58,
      'Trustworthiness': 63,
      'Business Continuity': 68
    },
    'Web 3.0': {
      'Emotional Connection': 68,
      'Ethical Foundations': 55,
      'Leadership Commitment': 60,
      'Trustworthiness': 50,
      'Business Continuity': 58
    }
  };
  
  // Top performer benchmarks (usually 15-20% higher than average)
  const topPerformerPremium = 18;
  
  // Default benchmarks if industry not found
  const defaultAverages = {
    'Emotional Connection': 64,
    'Ethical Foundations': 62,
    'Leadership Commitment': 60,
    'Trustworthiness': 65,
    'Business Continuity': 66
  };
  
  // Get the right industry averages or use defaults
  const averages = industryAverages[industry] || defaultAverages;
  
  // Format the your score data from scoreBreakdown
  return [
    {
      category: 'Emotional Connection',
      yourScore: scoreBreakdown.ea.percentage,
      industryAverage: averages['Emotional Connection'],
      topPerformerScore: Math.min(95, Math.round(averages['Emotional Connection'] * (1 + topPerformerPremium / 100)))
    },
    {
      category: 'Ethical Foundations',
      yourScore: scoreBreakdown.elv.percentage,
      industryAverage: averages['Ethical Foundations'],
      topPerformerScore: Math.min(95, Math.round(averages['Ethical Foundations'] * (1 + topPerformerPremium / 100)))
    },
    {
      category: 'Leadership Commitment',
      yourScore: scoreBreakdown.l.percentage,
      industryAverage: averages['Leadership Commitment'],
      topPerformerScore: Math.min(95, Math.round(averages['Leadership Commitment'] * (1 + topPerformerPremium / 100)))
    },
    {
      category: 'Trustworthiness',
      yourScore: scoreBreakdown.tb.percentage,
      industryAverage: averages['Trustworthiness'],
      topPerformerScore: Math.min(95, Math.round(averages['Trustworthiness'] * (1 + topPerformerPremium / 100)))
    },
    {
      category: 'Business Continuity',
      yourScore: scoreBreakdown.bc.percentage,
      industryAverage: averages['Business Continuity'],
      topPerformerScore: Math.min(95, Math.round(averages['Business Continuity'] * (1 + topPerformerPremium / 100)))
    }
  ];
}
